import { transformListingCompany, transformListingContact, transformListingOffice } from "services/api/settings/listings/transformations";
import { transformFleetVehicle, transformFleetVehicleRow } from "services/api/fleet/vehicles/transformations";
import {
  ListingChannelSummary,
  ListingLocation,
  ListingVehiclesSearchResult,
  MarketplaceDetails,
  VehicleListing,
  VehicleListingRow,
} from "./types";

/*---------------------------------------------------------------------------------------
    LISTING VEHICLES - SEARCH
  -----------------------------------------------------------------------------------------*/

export const transformListingVehiclesSearchResult = (res: any): ListingVehiclesSearchResult => ({
  vehicles: {
    data: (res.results || []).map(transformFleetVehicleRow),
    pagination: res.pagination,
  },
  listings: (res.listings || []).map(transformListingRow),
});

/*---------------------------------------------------------------------------------------
    LISTING - CHANNEL SUMMARY (row badge)
  -----------------------------------------------------------------------------------------*/

export const transformListingChannelSummary = (res: any): ListingChannelSummary => ({
  salesChannelId: res.salesChannelId,
  name: res.name,
  isActive: res.isActive,
  effectivePrice: res.effectivePrice ?? null,
});

/*---------------------------------------------------------------------------------------
    LISTING - SEARCH ROW
  -----------------------------------------------------------------------------------------*/

export const transformListingRow = (res: any): VehicleListingRow => ({
  id: res.id,
  fleetVehicleId: res.fleetVehicleId,
  status: res.status,
  basePrice: res.basePrice,
  baseTitle: res.baseTitle,
  published: res.published,
  created: res.created,
  activeChannelCount: res.activeChannelCount,
  channels: (res.channels ?? []).map(transformListingChannelSummary),
});

/*---------------------------------------------------------------------------------------
    LISTING - LOCATION
  -----------------------------------------------------------------------------------------*/

export const transformListingLocation = (res: any): ListingLocation => ({
  line1: res.line1,
  city: res.city,
  postalCode: res.postalCode,
  country: res.country,
  latitude: res.latitude,
  longitude: res.longitude,
});

/*---------------------------------------------------------------------------------------
    LISTING - MARKETPLACE DETAILS
  -----------------------------------------------------------------------------------------*/

export const transformMarketplaceDetails = (res: any): MarketplaceDetails => ({
  id: res.id ?? null,
  manufacturingYear: res.manufacturingYear ?? null,
  manufacturerId: res.manufacturerId ?? null,
  modelId: res.modelId ?? null,
  typeName: res.typeName ?? null,
  fuelTypeCode: res.fuelTypeCode ?? null,
  driveTypeCode: res.driveTypeCode ?? null,
  doors: res.doors ?? null,
  powerKw: res.powerKw ?? null,
  transmissionCode: res.transmissionCode ?? null,
  bodyShapeCode: res.bodyShapeCode ?? null,
  displacementCm3: res.displacementCm3 ?? null,
  modelYear: res.modelYear ?? null,
  seats: res.seats ?? null,
  suspensionCode: res.suspensionCode ?? null,
  colorId: res.colorId ?? null,
  metallic: res.metallic ?? false,
  tireWidth: res.tireWidth ?? null,
  tireHeight: res.tireHeight ?? null,
  tireDiameter: res.tireDiameter ?? null,
  kmTraveled: res.kmTraveled ?? null,
  vehicleConditionCode: res.vehicleConditionCode ?? null,
  fuelConsumption: res.fuelConsumption ?? null,
  co2Emission: res.co2Emission ?? null,
  ecoCategoryCode: res.ecoCategoryCode ?? null,
  hasGas: res.hasGas ?? false,
  garaged: res.garaged ?? false,
  inTrafficSinceYear: res.inTrafficSinceYear ?? null,
  firstRegistrationYearRH: res.firstRegistrationYearRH ?? null,
  registeredUntil: res.registeredUntil ?? null,
  warrantyCode: res.warrantyCode ?? null,
  extendedWarranty: res.extendedWarranty ?? false,
  g1ClubWarranty: res.g1ClubWarranty ?? false,
  ownerCountCode: res.ownerCountCode ?? null,
  serviceBook: res.serviceBook ?? false,
  chassisNumber: res.chassisNumber ?? null,
  videoCallViewing: res.videoCallViewing ?? false,
  internalCode: res.internalCode ?? null,
  airbagsCode: res.airbagsCode ?? null,
  airConditioningCode: res.airConditioningCode ?? null,
  carRadioCode: res.carRadioCode ?? null,
  equipment: res.equipment ?? {},
  priceIncludesImport: res.priceIncludesImport ?? null,
  paymentOptions: res.paymentOptions ?? {},
  locationCountyCode: res.locationCountyCode ?? null,
  locationMunicipalityCode: res.locationMunicipalityCode ?? null,
  locationSettlementCode: res.locationSettlementCode ?? null,
  locationManualAddress: res.locationManualAddress ?? null,
  locationDisplay: res.locationDisplay ?? null,
  contactUserIds: res.contactUserIds ?? [],
  locationOfficeIds: res.locationOfficeIds ?? [],
  contactId: res.contactId ?? null,
  contactPhone: res.contactPhone ?? null,
  additionalPhone: res.additionalPhone ?? null,
  youtubeUrl: res.youtubeUrl ?? null,
});

/*---------------------------------------------------------------------------------------
    LISTING - DETAIL
  -----------------------------------------------------------------------------------------*/

export const transformListing = (res: any): VehicleListing => ({
  id: res.id,
  fleetVehicleId: res.fleetVehicleId,
  fleetVehicleName: res.fleetVehicleName,
  status: res.status,
  basePrice: res.basePrice,
  baseTitle: res.baseTitle,
  baseDescription: res.baseDescription,
  published: res.published,
  created: res.created,
  channels: res.channels ?? [],
  // Vehicle iz GET /sales/listings/{id} stiže u BE shapeu iz `FleetVehicleFactory.PrepareVehicleModelAsync`
  // pa može direktno kroz postojeći transformFleetVehicle. Null ako BE nije
  // uspio dohvatiti (npr. obrisano vozilo).
  vehicle: res.vehicle ? transformFleetVehicle(res.vehicle) : null,
  // `id` mapiramo na uploadId jer galerija i wizard match-aju protiv
  // `channel.images.uploadId`. `url` dolazi gotov iz BE-a (Url.Action).
  fleetVehiclePictures: (res.fleetVehiclePictures ?? []).map((p: any) => ({
    id: p.upload?.id ?? p.id,
    url: p.url,
    name: p.upload?.originalFileName ?? p.name ?? "",
  })),
  location: res.location ? transformListingLocation(res.location) : null,
  // Marketplace detalji se na BE-u prosljeđuju kao zasebno polje (eager-load
  // u GET /v1/sales/listings/{id}). Ako još nije snimljeno, polje je null.
  marketplaceDetails: res.marketplaceDetails ? transformMarketplaceDetails(res.marketplaceDetails) : null,
  // Kontakt rezolvira backend iz postavki oglasa 2014 FE ga samo prikazuje.
  contacts: (res.contacts ?? []).map(transformListingContact),
  offices: (res.offices ?? []).map(transformListingOffice),
  company: res.company ? transformListingCompany(res.company) : null,
});
